import React from 'react';
import { Card, Col, Row, Typography, Button, Space, Statistic, Progress, Avatar, Badge, Timeline } from 'antd';
import { 
  UserOutlined, 
  ProjectOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined,
  TeamOutlined,
  RocketOutlined,
  TrophyOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;

const Home = () => {
  const user = useSelector((state) => state.user);
  const projects = useSelector((state) => state.projects);
  const users = useSelector((state) => state.users);

  const totalTasks = projects?.reduce((acc, project) => {
    return acc + (project.lists?.reduce((listAcc, list) => {
      return listAcc + (list?.tasks?.length || 0);
    }, 0) || 0);
  }, 0) || 0;

  const completedTasks = projects?.reduce((acc, project) => {
    return acc + (project.lists?.reduce((listAcc, list) => {
      return listAcc + (list?.tasks?.filter(task => task?.status === 'completed').length || 0);
    }, 0) || 0);
  }, 0) || 0;

  const taskCompletionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const recentActivities = [
    {
      time: '2 hours ago',
      description: 'Completed task: Website redesign',
      icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
    },
    {
      time: '4 hours ago',
      description: 'Started new project: Mobile app development',
      icon: <RocketOutlined style={{ color: '#1890ff' }} />
    },
    {
      time: '1 day ago',
      description: 'Team meeting scheduled',
      icon: <CalendarOutlined style={{ color: '#722ed1' }} />
    },
    {
      time: '2 days ago',
      description: 'Project milestone achieved',
      icon: <TrophyOutlined style={{ color: '#faad14' }} />
    }
  ];

  return (
    <div style={{ padding: '24px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Welcome Section */}
      <div style={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        padding: '32px',
        marginBottom: '24px',
        color: 'white'
      }}>
        <Row align="middle" gutter={24}>
          <Col flex="auto">
            <Title level={2} style={{ color: 'white', margin: 0 }}>
              Welcome back, {user?.firstName || 'User'}! 👋
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', margin: '8px 0 0 0' }}>
              Ready to tackle your goals today? Let's make it productive!
            </Paragraph>
          </Col>
          <Col>
            <Avatar size={64} icon={<UserOutlined />} />
          </Col>
        </Row>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Total Projects"
              value={projects?.length || 0}
              prefix={<ProjectOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Total Tasks"
              value={totalTasks}
              prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Team Members"
              value={users?.length || 0}
              prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card style={{ borderRadius: '8px' }}>
            <Statistic
              title="Completion Rate"
              value={taskCompletionRate}
              suffix="%"
              prefix={<TrophyOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Progress Overview */}
        <Col xs={24} lg={12}>
          <Card 
            title="Progress Overview" 
            style={{ borderRadius: '8px', height: '400px' }}
            extra={<Link to="/tasks">View All Tasks</Link>}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div>
                <Text strong>Overall Task Completion</Text>
                <Progress 
                  percent={taskCompletionRate} 
                  strokeColor={{
                    '0%': '#667eea',
                    '100%': '#764ba2',
                  }}
                  style={{ marginTop: '8px' }}
                />
              </div>
              
              <div>
                <Text strong>Weekly Goal Progress</Text>
                <Progress 
                  percent={75} 
                  strokeColor="#52c41a"
                  style={{ marginTop: '8px' }}
                />
              </div>
              
              <div>
                <Text strong>Monthly Targets</Text>
                <Progress 
                  percent={60} 
                  strokeColor="#faad14"
                  style={{ marginTop: '8px' }}
                />
              </div>

              <div style={{ 
                background: '#f8f9fa', 
                padding: '16px', 
                borderRadius: '8px',
                marginTop: '16px'
              }}>
                <Text type="secondary">
                  🎯 You're making great progress! Keep up the momentum.
                </Text>
              </div>
            </Space>
          </Card>
        </Col>

        {/* Recent Activity */}
        <Col xs={24} lg={12}>
          <Card 
            title="Recent Activity" 
            style={{ borderRadius: '8px', height: '400px' }}
            extra={<Link to="/everything">View All</Link>}
          >
            <Timeline
              items={recentActivities.map((activity, index) => ({
                key: index,
                dot: activity.icon,
                children: (
                  <div>
                    <Text>{activity.description}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {activity.time}
                    </Text>
                  </div>
                )
              }))}
            />
          </Card>
        </Col>
      </Row>

      {/* Quick Actions */}
      <Card 
        title="Quick Actions" 
        style={{ borderRadius: '8px', marginTop: '24px' }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Button 
              type="primary" 
              size="large" 
              block
              icon={<ProjectOutlined />}
              style={{
                height: '56px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
            >
              <Link to="/create/project" style={{ color: 'white' }}>
                Create New Project
              </Link>
            </Button>
          </Col>
          <Col xs={24} sm={8}>
            <Button 
              size="large" 
              block
              icon={<CheckCircleOutlined />}
              style={{
                height: '56px',
                borderRadius: '8px',
                borderColor: '#52c41a',
                color: '#52c41a'
              }}
            >
              <Link to="/tasks" style={{ color: '#52c41a' }}>
                View My Tasks
              </Link>
            </Button>
          </Col>
          <Col xs={24} sm={8}>
            <Button 
              size="large" 
              block
              icon={<TeamOutlined />}
              style={{
                height: '56px',
                borderRadius: '8px',
                borderColor: '#722ed1',
                color: '#722ed1'
              }}
            >
              <Link to="/club" style={{ color: '#722ed1' }}>
                Join Team Meeting
              </Link>
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default Home;